/**
 * Google Analytics and Ads conversion tracking utilities
 */

// GA4 Measurement ID
const GA_MEASUREMENT_ID = "G-HHCSC08RQG";

/**
 * Track a page view in Google Analytics
 * Used for SPA navigation tracking
 * @param path - The page path (e.g., "/dashboard/companies/xxx/agents")
 */
export const trackPageView = (path: string): void => {
  try {
    if (typeof window !== "undefined" && (window as any).gtag) {
      (window as any).gtag("config", GA_MEASUREMENT_ID, {
        page_path: path,
      });
    }
  } catch (error) {
    // Silently fail - don't disrupt the user experience
    console.error("Failed to track page view:", error);
  }
};

/**
 * Track a purchase conversion in Google Ads
 * @param value - The purchase value in USD
 * @param transactionId - Unique transaction ID to prevent duplicate conversions
 * @param email - User's email for Enhanced Conversions (gtag auto SHA-256 hashes)
 * @returns Promise that resolves when the conversion is sent (or times out after 2s)
 */
export const trackGoogleAdsConversion = (
  value: number,
  transactionId: string,
  email?: string
): Promise<void> => {
  return new Promise((resolve) => {
    try {
      if (typeof window !== "undefined" && (window as any).gtag) {
        // Set a timeout to ensure we don't block forever if gtag fails
        const timeoutId = setTimeout(() => {
          console.log("Google Ads conversion timeout - proceeding anyway");
          resolve();
        }, 2000);

        // Enhanced Conversions: provide user-provided data so Google can match
        // the conversion to an ad click. gtag will SHA-256 hash plain values.
        if (email) {
          (window as any).gtag("set", "user_data", {
            email: email.trim().toLowerCase(),
          });
        }

        (window as any).gtag("event", "conversion", {
          send_to: "AW-17332824102/XuCsCKeAru0aEKbQ98hA",
          value: value,
          currency: "USD",
          transaction_id: transactionId,
          event_callback: () => {
            clearTimeout(timeoutId);
            console.log("Google Ads conversion tracked:", { value, transactionId });
            resolve();
          },
        });
      } else {
        resolve();
      }
    } catch (error) {
      // Silently fail - don't disrupt the payment process
      console.error("Failed to track Google Ads conversion:", error);
      resolve();
    }
  });
};

/**
 * Track a purchase conversion in Reddit Ads
 * @param value - The purchase value in USD
 * @param transactionId - Unique transaction ID to prevent duplicate conversions
 */
export const trackRedditConversion = (
  value: number,
  transactionId: string
): void => {
  try {
    if (typeof window !== "undefined" && (window as any).rdt) {
      (window as any).rdt("track", "Purchase", {
        value: value,
        currency: "USD",
        transactionId: transactionId,
      });
      console.log("Reddit conversion tracked:", { value, transactionId });
    }
  } catch (error) {
    // Silently fail - don't disrupt the payment process
    console.error("Failed to track Reddit conversion:", error);
  }
};

/**
 * Track a purchase conversion across all ad platforms
 * @param value - The purchase value in USD
 * @param transactionId - Unique transaction ID to prevent duplicate conversions
 * @returns Promise that resolves when all conversions are sent
 */
/**
 * Track a GA4 purchase event for ecommerce reporting.
 * Separate from the Google Ads conversion — GA4 and Ads are different systems.
 */
export const trackGA4Purchase = (
  value: number,
  transactionId: string,
  email?: string
): void => {
  try {
    if (typeof window !== "undefined" && (window as any).gtag) {
      if (email) {
        (window as any).gtag("set", "user_data", {
          email: email.trim().toLowerCase(),
        });
      }
      (window as any).gtag("event", "purchase", {
        send_to: GA_MEASUREMENT_ID,
        transaction_id: transactionId,
        value: value,
        currency: "USD",
        items: [
          {
            item_id: "subscription",
            item_name: "Subscription",
            price: value,
            quantity: 1,
          },
        ],
      });
      console.log("GA4 purchase tracked:", { value, transactionId });
    }
  } catch (error) {
    console.error("Failed to track GA4 purchase:", error);
  }
};

export const trackPurchaseConversion = async (
  value: number,
  transactionId: string,
  email?: string
): Promise<void> => {
  // Track Reddit synchronously (no callback support)
  trackRedditConversion(value, transactionId);
  // Track GA4 purchase event for ecommerce reporting
  trackGA4Purchase(value, transactionId, email);
  // Wait for Google Ads conversion to complete
  await trackGoogleAdsConversion(value, transactionId, email);
};
